import { useState, useEffect, useRef } from 'react'
import { IRevenueSpendByAccountFilters, IFilterConfig, EDatePickerMode } from '../types'
import { constructNewFilterFromConfig, constructUserFiltersFromUrl } from '../components/filters'
import * as lib from '../lib'
import { EFilterOperator, EFilterType, EFilterSpecOperator } from '../types/enum'
import { useAtomValue } from 'jotai'
import * as state from '../states'
import { useSearchParams } from 'react-router'
import dt from 'dayjs'
import * as helpers from '../helpers'
import { ALL_LANG_KEY } from '../types/lang'
import { useTranslation } from 'react-i18next'

type IUseFiltersRevenueSpendByAccountPage = {
	filter: [string, any][];
	send: boolean;
	activeFiltersCount: number;
	filterFillFactor: number;
	isFiltersPanelOpen: boolean;
	setIsFiltersPanelOpen: (value: (((prevState: boolean) => boolean) | boolean)) => void;
	revenueSpendByAccountFiltersConfig: IFilterConfig<keyof IRevenueSpendByAccountFilters>[];
	defaultFiltersValue: IRevenueSpendByAccountFilters;
	initialFiltersValue: IRevenueSpendByAccountFilters;
	clearChildFilters: number;
	setUserFilters: (value: (((prevState: (IRevenueSpendByAccountFilters | null)) => (IRevenueSpendByAccountFilters | null)) | IRevenueSpendByAccountFilters | null)) => void;
}

const [startDate, endDate] = helpers.getDateRange(EDatePickerMode.currentMonth)

const REVENUE_SPEND_BY_ACCOUNT_FILTERS_DEFAULT: IRevenueSpendByAccountFilters = {
	accountId: '',
	'account.auth.login': '',
	'account.extra.keitaroLogin': '',
	'role.inGroup': '',
	at: [startDate ? startDate.format('YYYY-MM-DD') : '', endDate ? endDate.format('YYYY-MM-DD') : '']
	//at: [dt().startOf('month').format('YYYY-MM-DD'), dt().startOf('day').format('YYYY-MM-DD')]
}


export const useRevenueSpendByAccountFiltersPage = (buyer: number): IUseFiltersRevenueSpendByAccountPage => {
	const { t } = useTranslation()
	const isFirstRender = useRef<boolean>(true)
	const [buyerPrev, setBuyerPrev] = useState<number>(buyer)

	const profile = useAtomValue(state.profileAtom)
	const isBuyer = useAtomValue(state.isBuyerAtom)

	const [searchParams, setSearchParams] = useSearchParams()

	const urlFilter = (() => {
		try {
			return lib.filterSearchParams(searchParams.get('f')) ?? []
		} catch (error) {
			console.error(error)
			return []
		}
	})()

	const [filter, setFilter] = useState<[string, any][]>([...urlFilter]) // main filter with mixins
	const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState<boolean>(false)
	const [activeFiltersCount, setActiveFiltersCount] = useState<number>(0)

	const [clearChildFilters, setClearChildFilters] = useState<number>(0)
	const [send, setSend] = useState<boolean>(false)
	const [userFilters, setUserFilters] = useState<IRevenueSpendByAccountFilters | null>(null)

	const filterFillFactor = !!userFilters ? (100 * activeFiltersCount / Object.keys(userFilters).length) : 0

	const revenueSpendByAccountFiltersConfig: IFilterConfig<keyof IRevenueSpendByAccountFilters>[] = [
		/*{
			name: 'accountId',
			label: 'Account ID',
			type: EFilterType.input,
			attr: { type: 'number' },
			operator: EFilterOperator.isIntLike,
			specOperator: EFilterSpecOperator.everywhere,
			styles: { width: '200px' }
		},
		{
			name: 'account.auth.login',
			label: 'Account',
			type: EFilterType.input,
			operator: EFilterOperator.isLike,
			specOperator: EFilterSpecOperator.everywhere,
			styles: { width: '200px' }
		},
		{
			name: 'account.extra.keitaroLogin',
			label: 'KT Group',
			type: EFilterType.input,
			operator: EFilterOperator.isLike,
			specOperator: EFilterSpecOperator.everywhere,
			styles: { width: '200px' }
		},
		{
			name: 'role.inGroup',
			label: 'Account in Group',
			type: EFilterType.input,
			operator: EFilterOperator.isLike,
			specOperator: EFilterSpecOperator.everywhere,
			styles: { width: '200px' }
		},*/
		{
			name: 'at',
			label: t(ALL_LANG_KEY.revenue_spend_by_account_list_table.column_date),
			type: EFilterType.dateRange,
			operator: EFilterOperator.isBetween,
			styles: { width: '280px' },
			isClearable: false
		}
	]

	const initialUserFilters = constructUserFiltersFromUrl(
		urlFilter,
		revenueSpendByAccountFiltersConfig,
		REVENUE_SPEND_BY_ACCOUNT_FILTERS_DEFAULT
	)

	useEffect(() => {
		let _userFilters = userFilters
		//console.log('hook.useRevenueSpendByAccountFiltersPage useEffect::START:: ', { send, buyer, buyerPrev, userFilters, _userFilters, filter })
		if (send && buyerPrev !== buyer) {
			// reset filters [RFC] onBuyer (menu tabs) changed
			setBuyerPrev(buyer)
			//console.log('!!! clear main filter:: send && buyerPrev !== buyer !!!')
			setFilter([]) // clear main filter with mixins
			_userFilters = REVENUE_SPEND_BY_ACCOUNT_FILTERS_DEFAULT
			setUserFilters(REVENUE_SPEND_BY_ACCOUNT_FILTERS_DEFAULT)
			setClearChildFilters(prev => prev + 1) // clearAllChildFilters command-flag
		}

		if (!!_userFilters) {
			setSend(true)
			//console.log('hook.useFiltersOfferScopePage useEffect::MIDDLE:: ', { userFilters, _userFilters, filter })
			const { newFilter, activeFiltersCnt } = constructNewFilterFromConfig(
				_userFilters,
				revenueSpendByAccountFiltersConfig
			)
			const newFilterStr = lib.filterSetup([...newFilter ?? []])

			setSearchParams((prev) => {
				if (newFilterStr) {
					prev.set('f', newFilterStr)
				} else {
					prev.delete('f')
				}

				// after first pageLoad/pageRefresh resetting is not needed
				if (!isFirstRender.current) {
					prev.delete('page')
				} else {
					isFirstRender.current = false
				}

				if (!!isBuyer && `${isBuyer}` !== prev.get('buyer')) {
					prev.set('buyer', encodeURIComponent(buyer))
				}

				return prev
			})

			if (buyer) {
				newFilter.push([`accountId${EFilterOperator.isEqual}`, buyer])
			}

			setFilter(newFilter)
			setActiveFiltersCount(activeFiltersCnt)

			// to open FiltersPanel if there were previously active filters on the page [onRefreshPage case]
			if (activeFiltersCnt > 0) {
				setIsFiltersPanelOpen(true)
			}
			console.log('hook.useRevenueSpendByAccountFiltersPage:: useEffect::END:: ', {
				userFilters,
				_userFilters,
				filter,
				newFilter,
				newFilterStr
			})
		}
	}, [userFilters, buyer])

	return {
		filter,
		send,
		activeFiltersCount,
		filterFillFactor,
		isFiltersPanelOpen,
		setIsFiltersPanelOpen,
		revenueSpendByAccountFiltersConfig,
		defaultFiltersValue: REVENUE_SPEND_BY_ACCOUNT_FILTERS_DEFAULT,
		initialFiltersValue: initialUserFilters,
		clearChildFilters,
		setUserFilters
	}
}